import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withKeys as _withKeys, Transition as _Transition, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex items-center gap-x-1 leading-normal" }
const _hoisted_2 = { class: "font-medium" }
const _hoisted_3 = { class: "text-gray-500" }
const _hoisted_4 = { class: "flex justify-between gap-x-8 px-4 py-5" }
const _hoisted_5 = { class: "flex flex-col" }
const _hoisted_6 = { class: "text-base font-medium" }
const _hoisted_7 = { class: "text-sm text-gray-500 max-w-sm" }
const _hoisted_8 = { class: "flex justify-end" }
const _hoisted_9 = {
  key: 0,
  class: "ml-1 text-sm flex-shrink-0 font-medium flex items-center justify-center p-1 rounded-full border border-primary-800 min-w-5 h-5"
}
const _hoisted_10 = {
  role: "tree",
  "aria-orientation": "horizontal",
  class: "grid grid-cols-2 divide-x h-full max-h-full overflow-hidden border-t"
}
const _hoisted_11 = {
  class: "flex flex-col items-stretch min-h-0 max-h-full",
  role: "group"
}
const _hoisted_12 = { class: "sticky z-10 top-0 bg-white font-medium text-base border-b px-4 py-2" }
const _hoisted_13 = { class: "max-h-full overflow-y-auto" }
const _hoisted_14 = ["aria-label", "aria-expanded", "aria-setsize", "aria-posinset", "onKeydown", "onMouseover", "onClick"]
const _hoisted_15 = { class: "flex grow gap-x-3 items-center justify-between max-w-full min-w-0" }
const _hoisted_16 = { class: "grow select-none truncate" }
const _hoisted_17 = {
  class: "flex flex-col items-stretch min-h-0 max-h-full",
  role: "group"
}
const _hoisted_18 = { class: "sticky z-10 top-0 bg-white font-medium text-base border-b px-4 py-2" }
const _hoisted_19 = {
  key: 0,
  class: "max-h-full overflow-y-auto"
}
const _hoisted_20 = ["aria-label", "aria-setsize", "aria-posinset"]
const _hoisted_21 = { class: "flex gap-x-3 items-center px-3.5 py-3.5 w-full cursor-pointer hover:bg-gray-100/70 focus:bg-gray-100/70 transition-colors" }
const _hoisted_22 = { class: "grow flex items-center justify-between gap-x-2 select-none max-w-full min-w-0" }
const _hoisted_23 = { class: "flex items-center gap-x-1.5 min-w-0" }
const _hoisted_24 = { class: "whitespace-nowrap truncate min-w-0" }
const _hoisted_25 = { class: "py-3 px-4 border-t" }
const _hoisted_26 = {
  key: 0,
  class: "text-red-500 text-right font-medium"
}
const _hoisted_27 = { class: "pb-4" }
const _hoisted_28 = { class: "flex justify-end" }
const _hoisted_29 = { class: "pt-2" }
const _hoisted_30 = { class: "w-full" }
const _hoisted_31 = { class: "text-left font-medium text-gray-400 pl-4 pr-2 pt-2 pb-4" }
const _hoisted_32 = { class: "text-left font-medium text-gray-400 pl-4 pr-2 pt-2 pb-4" }
const _hoisted_33 = { class: "border border-gray-100" }
const _hoisted_34 = { class: "pl-4 pr-2 py-2" }
const _hoisted_35 = { class: "pl-4 pr-2 py-2" }
const _hoisted_36 = { class: "text-right pl-4 pr-2 py-2" }

import { computed, ref, nextTick } from 'vue';
import { ChevronRight, ChevronDown, Trash2 } from 'lucide-vue-next';
import { VisuallyHidden } from 'radix-vue';
import cloneDeep from 'lodash-es/cloneDeep';
import CustomCheckbox from '@/components/ui/CustomCheckbox.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { StudyPeriodCategory } from '@/types';
import ExpandCollapseTransition from '@/components/common/ExpandCollapseTransition.vue';

interface Props {
    categories: StudyPeriodCategory[];
    errorMessage: string | null;
}

interface ManageOption {
    id: number;
    name: string;
    categoryName: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CategoryPicker',
  props: /*@__PURE__*/_mergeModels({
    categories: {},
    errorMessage: {}
  }, {
    "selectedComparisonType": { required: true },
    "selectedComparisonTypeModifiers": {},
    "selectedCategoryOptions": { required: true },
    "selectedCategoryOptionsModifiers": {},
  }),
  emits: ["update:selectedComparisonType", "update:selectedCategoryOptions"],
  setup(__props: any) {

const props = __props;

const selectedComparisonType = _useModel<'within' | 'between'>(__props, 'selectedComparisonType');
const selectedCategoryOptions = _useModel<number[]>(__props, 'selectedCategoryOptions');

const isCategoryPickerOpen = ref(false);

const comparisonOptions = computed(() => [
    {
        label: i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.comparisonType.multipleTeams.label'),
        description: i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.comparisonType.multipleTeams.description'),
        value: 'between',
    },
    {
        label: i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.comparisonType.singleTeam.label'),
        description: i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.comparisonType.singleTeam.description'),
        value: 'within',
    },
]);

const categoryOptions = computed(
    () =>
        props.categories.map((category) => {
            const options = category.options.map((option) => ({
                ...option,
                checked: selectedCategoryOptions.value.includes(option.id),
            }));

            const checkedOptions = options.filter((option) => option.checked);

            return {
                ...category,
                options,
                checked: checkedOptions.length === options.length,
                indeterminate: checkedOptions.length > 0 && checkedOptions.length < options.length,
            };
        }) ?? []
);
const selectedCategoryIndex = ref(0);
const activeCategory = computed(() => categoryOptions.value[selectedCategoryIndex.value]);

function selectActiveCategory(index: number) {
    selectedCategoryIndex.value = index;
}

function toggleCategory(category: any) {
    if (category.checked) {
        const optionIdsToRemove = category.options.filter((o: any) => o.checked).map((o: any) => o.id);

        selectedCategoryOptions.value = selectedCategoryOptions.value.filter((option) => !optionIdsToRemove.includes(option));
    } else {
        const newSelectedCategoryOptions = new Set([...selectedCategoryOptions.value, ...category.options.map((o: any) => o.id)]);

        selectedCategoryOptions.value = [...newSelectedCategoryOptions];
    }
}

function toggleCategoryOption(option: any) {
    if (selectedCategoryOptions.value.includes(option.id)) {
        selectedCategoryOptions.value = selectedCategoryOptions.value.filter((o) => o !== option.id);
    } else {
        selectedCategoryOptions.value = [...selectedCategoryOptions.value, option.id];
    }
}

const selectedChartCategoriesCount = computed(
    () => selectedCategoryOptions.value.filter((optionId) => props.categories.some((c) => c.options.some((option) => option.id === optionId && option.type === 'chart'))).length
);
const selectedTableCategoriesCount = computed(
    () => selectedCategoryOptions.value.filter((optionId) => props.categories.some((c) => c.options.some((option) => option.id === optionId && option.type === 'table'))).length
);

const displayValue = computed(() => {
    const texts = [];

    if (selectedChartCategoriesCount.value > 0) {
        texts.push(
            `${selectedChartCategoriesCount.value} ${
                selectedChartCategoriesCount.value === 1
                    ? i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.texts.chart')
                    : i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.texts.charts')
            }`
        );
    }

    if (selectedTableCategoriesCount.value > 0) {
        texts.push(
            `${selectedTableCategoriesCount.value} ${
                selectedTableCategoriesCount.value === 1
                    ? i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.texts.table')
                    : i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.texts.tables')
            }`
        );
    }

    return texts.length ? texts.join(', ') : i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.texts.noSelection');
});

const selectedOptionsToManage = computed<ManageOption[]>(() => {
    const options: ManageOption[] = [];

    for (const category of categoryOptions.value) {
        for (const option of category.options) {
            if (selectedCategoryOptions.value.includes(option.id)) {
                options.push({
                    id: option.id,
                    name: option.name,
                    categoryName: category.name,
                });
            }
        }
    }

    return options;
});

const showManageDialog = ref(false);

const optionsToManage = ref<ManageOption[]>([]);

async function openManageDialog() {
    optionsToManage.value = cloneDeep(selectedOptionsToManage.value);
    await nextTick();
    showManageDialog.value = true;
}

function removeManageOption(optionId: number) {
    optionsToManage.value = optionsToManage.value.filter((o) => o.id !== optionId);
}

function saveManageOptions() {
    selectedCategoryOptions.value = optionsToManage.value.map((o) => o.id);
    showManageDialog.value = false;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Popover), {
    open: isCategoryPickerOpen.value,
    "onUpdate:open": _cache[3] || (_cache[3] = ($event: any) => ((isCategoryPickerOpen).value = $event))
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(PopoverTrigger), { "as-child": "" }, {
        default: _withCtx(() => [
          _createElementVNode("button", {
            class: _normalizeClass(["mt-3 w-full min-h-[46px] flex items-center justify-between gap-x-3 bg-white border rounded-md py-2.5 px-3.5 text-left transition-colors", { 'border-red-500': _ctx.errorMessage }])
          }, [
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("span", _hoisted_2, _toDisplayString(_ctx.$t('views.monitoring.teamComparison.fields.categoryPicker.label')) + ":", 1),
              _createElementVNode("span", _hoisted_3, _toDisplayString(displayValue.value), 1)
            ]),
            _createVNode(_unref(ChevronDown), {
              class: _normalizeClass(["flex-shrink-0 h-5 w-5 transition-transform", { 'rotate-180': isCategoryPickerOpen.value }]),
              "aria-hidden": "true"
            }, null, 8, ["class"])
          ], 2)
        ]),
        _: 1
      }),
      _createVNode(_unref(PopoverContent), {
        "trigger-width": "",
        align: "start",
        class: "flex flex-col p-0 h-[516px] overflow-hidden"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", null, [
              _createVNode(_unref(RadioGroup), {
                modelValue: selectedComparisonType.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedComparisonType).value = $event)),
                class: "gap-x-5"
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(comparisonOptions.value, (option) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: option.value,
                      class: "flex items-start cursor-pointer"
                    }, [
                      _createVNode(_unref(RadioGroupItem), {
                        id: `comparison-type-option-id-${option.value}`,
                        value: option.value.toString()
                      }, null, 8, ["id", "value"]),
                      _createVNode(_unref(Label), {
                        for: `comparison-type-option-id-${option.value}`,
                        class: "font-normal cursor-pointer pl-2 -mt-[2.5px]"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_5, [
                            _createElementVNode("span", _hoisted_6, _toDisplayString(option.label), 1),
                            _createElementVNode("span", _hoisted_7, _toDisplayString(option.description), 1)
                          ])
                        ]),
                        _: 2
                      }, 1032, ["for"])
                    ]))
                  }), 128))
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(_unref(Button), {
                variant: "secondary",
                size: "sm",
                disabled: !selectedOptionsToManage.value.length,
                onClick: openManageDialog
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamComparison.fields.categoryPicker.buttons.manageSelected')) + " ", 1),
                  (selectedOptionsToManage.value.length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(selectedOptionsToManage.value.length), 1))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["disabled"])
            ])
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("div", _hoisted_12, _toDisplayString(_ctx.$t('views.monitoring.teamComparison.fields.categoryPicker.groups.category')), 1),
              _createElementVNode("div", _hoisted_13, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categoryOptions.value, (category, categoryIndex) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: category.id,
                    class: "flex items-center cursor-pointer",
                    role: "treeitem",
                    "aria-label": category.name,
                    "aria-level": "1",
                    "aria-expanded": selectedCategoryIndex.value === categoryIndex,
                    "aria-setsize": categoryOptions.value.length,
                    "aria-posinset": categoryIndex + 1,
                    tabindex: "0",
                    onKeydown: _withKeys(($event: any) => (selectActiveCategory(categoryIndex)), ["enter"]),
                    onMouseover: ($event: any) => (selectActiveCategory(categoryIndex)),
                    onClick: ($event: any) => (selectActiveCategory(categoryIndex))
                  }, [
                    _createElementVNode("label", {
                      class: _normalizeClass(["flex gap-x-3 items-center px-3.5 py-3.5 w-full cursor-pointer transition-colors", {
                                    'hover:bg-gray-100/70 focus:bg-gray-100/70': selectedCategoryIndex.value !== categoryIndex,
                                    'bg-primary-300 bg-opacity-30': selectedCategoryIndex.value === categoryIndex,
                                }])
                    }, [
                      _createVNode(CustomCheckbox, {
                        checked: category.checked,
                        indeterminate: category.indeterminate,
                        "disable-transition": "",
                        class: "flex-shrink-0",
                        "onUpdate:checked": ($event: any) => (toggleCategory(category))
                      }, null, 8, ["checked", "indeterminate", "onUpdate:checked"]),
                      _createElementVNode("div", _hoisted_15, [
                        _createElementVNode("span", _hoisted_16, _toDisplayString(category.name), 1),
                        _createVNode(_unref(ChevronRight), { class: "flex-shrink-0 h-4 w-4 -mr-1" })
                      ])
                    ], 2)
                  ], 40, _hoisted_14))
                }), 128))
              ])
            ]),
            _createElementVNode("div", _hoisted_17, [
              _createElementVNode("div", _hoisted_18, _toDisplayString(_ctx.$t('views.monitoring.teamComparison.fields.categoryPicker.groups.selectView')), 1),
              _createVNode(_Transition, {
                "enter-active-class": "transition duration-100 ease-out",
                "enter-from-class": "opacity-0",
                "enter-to-class": "opacity-100",
                "leave-active-class": "transition duration-100 ease-in",
                "leave-from-class": "opacity-0",
                "leave-to-class": "opacity-0",
                mode: "out-in"
              }, {
                default: _withCtx(() => [
                  (activeCategory.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeCategory.value.options, (option, optionIndex) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: option.id,
                            role: "treeitem",
                            "aria-label": option.name,
                            "aria-level": "2",
                            "aria-setsize": activeCategory.value.options.length,
                            "aria-posinset": optionIndex + 1,
                            class: "flex items-center cursor-pointer",
                            tabindex: "0"
                          }, [
                            _createElementVNode("label", _hoisted_21, [
                              _createVNode(CustomCheckbox, {
                                checked: option.checked,
                                "disable-transition": "",
                                class: "flex-shrink-0",
                                "onUpdate:checked": ($event: any) => (toggleCategoryOption(option))
                              }, null, 8, ["checked", "onUpdate:checked"]),
                              _createElementVNode("div", _hoisted_22, [
                                _createElementVNode("div", _hoisted_23, [
                                  _createElementVNode("div", _hoisted_24, _toDisplayString(option.name), 1)
                                ])
                              ])
                            ])
                          ], 8, _hoisted_20))
                        }), 128))
                      ]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ])
          ]),
          _createElementVNode("div", _hoisted_25, [
            _createVNode(ExpandCollapseTransition, null, {
              default: _withCtx(() => [
                (_ctx.errorMessage)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_26, [
                      _createElementVNode("div", _hoisted_27, _toDisplayString(_ctx.errorMessage), 1)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_28, [
              _createVNode(_unref(Button), {
                class: "w-44",
                disabled: !!_ctx.errorMessage,
                onClick: _cache[1] || (_cache[1] = ($event: any) => (isCategoryPickerOpen.value = false))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamComparison.fields.categoryPicker.buttons.saveSelected')), 1)
                ]),
                _: 1
              }, 8, ["disabled"])
            ])
          ]),
          _createVNode(_unref(Dialog), {
            modelValue: showManageDialog.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showManageDialog).value = $event)),
            class: "sm:max-w-[600px]"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(DialogHeader), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(DialogTitle), null, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamComparison.fields.categoryPicker.dialogs.manageSelected.title')), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(VisuallyHidden), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(DialogDescription), null, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamComparison.fields.categoryPicker.dialogs.manageSelected.description')), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createElementVNode("div", _hoisted_29, [
                _createElementVNode("table", _hoisted_30, [
                  _createElementVNode("thead", null, [
                    _createElementVNode("tr", null, [
                      _createElementVNode("th", _hoisted_31, _toDisplayString(_ctx.$t('views.monitoring.teamComparison.fields.categoryPicker.dialogs.manageSelected.table.headers.category')), 1),
                      _createElementVNode("th", _hoisted_32, _toDisplayString(_ctx.$t('views.monitoring.teamComparison.fields.categoryPicker.dialogs.manageSelected.table.headers.options')), 1),
                      _cache[4] || (_cache[4] = _createElementVNode("th", null, null, -1))
                    ])
                  ]),
                  _createElementVNode("tbody", _hoisted_33, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(optionsToManage.value, (option) => {
                      return (_openBlock(), _createElementBlock("tr", {
                        key: option.id,
                        class: "odd:bg-gray-100"
                      }, [
                        _createElementVNode("td", _hoisted_34, _toDisplayString(option.categoryName), 1),
                        _createElementVNode("td", _hoisted_35, _toDisplayString(option.name), 1),
                        _createElementVNode("td", _hoisted_36, [
                          _createVNode(_unref(Button), {
                            variant: "ghost",
                            size: "icon",
                            class: "text-red-500 hover:text-red-600 hover:bg-red-100",
                            onClick: ($event: any) => (removeManageOption(option.id))
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(Trash2), { class: "w-4 h-4" })
                            ]),
                            _: 2
                          }, 1032, ["onClick"])
                        ])
                      ]))
                    }), 128))
                  ])
                ])
              ]),
              _createVNode(_unref(DialogFooter), { class: "flex sm:justify-center pt-2" }, {
                default: _withCtx(() => [
                  _createVNode(_unref(DialogClose), { "as-child": "" }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Button), {
                        variant: "ghost",
                        size: "lg",
                        class: "min-w-44"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamComparison.fields.categoryPicker.dialogs.manageSelected.buttons.cancel')), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(Button), {
                    size: "lg",
                    class: "min-w-44",
                    onClick: saveManageOptions
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamComparison.fields.categoryPicker.dialogs.manageSelected.buttons.save')), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["modelValue"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})